import React from "react";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { usePiopiy } from "../../context/PiopiyContext";

/**
 * ChatHeaderCallButton — the 📞 button in the chat header.
 *
 * Iter 133 (Apr 30, 2026) — now powered by TeleCMI CHUB:
 *   1. Agent clicks → backend hits CHUB `/v1/agentConnect`
 *   2. CHUB rings the agent's TeleCMI softphone FIRST (desktop/mobile)
 *   3. Once agent answers, CHUB dials the customer and bridges both legs
 *   4. Post-call CDR arrives via /api/webhooks/telecmi
 *
 * Requirements for this button to work:
 *   • Agent has CHUB credentials saved (Settings → Telecmi)
 *   • Agent has their TeleCMI softphone app running (desktop or mobile)
 *
 * Keeps the `chat-header-call-btn` testid so existing automation works.
 */

const ChatHeaderCallButton = ({ phone, customerName, leadId }) => {
  const { creds, call, makeCall } = usePiopiy();

  const onClick = () => {
    if (!phone) {
      toast.error("No phone number on this lead");
      return;
    }
    if (!creds?.configured) {
      toast.error("CHUB not set up yet — open Settings → Telecmi to add your agent ID + password");
      return;
    }
    if (call) {
      toast.error("Call trigger already pending — answer your softphone");
      return;
    }
    makeCall(phone, { leadId, customerName });
  };

  const disabled = !creds?.configured || !!call;
  const title = !creds?.configured
    ? "CHUB not configured — open Settings → Telecmi"
    : call
      ? "Call trigger pending — answer your softphone"
      : `Call ${phone} — rings your TeleCMI softphone, then dials customer`;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`btn-icon transition-colors ${
        disabled
          ? "opacity-40 cursor-not-allowed"
          : "hover:bg-emerald-50 hover:text-emerald-600"
      }`}
      title={title}
      data-testid="chat-header-call-btn"
    >
      <PhoneCall size={15} />
    </button>
  );
};

export default ChatHeaderCallButton;
